import { useSyncExternalStore } from "react";
import type { Locale } from "@/api/types";

/**
 * What the app knows about maintenance mode. `enabled` + `blocksReads` means the
 * whole signed-in API is closed and the login page is closed: show the
 * maintenance page. `enabled` alone means only writes are refused.
 *
 * Nothing here signs anyone out. A maintenance answer is not an authentication
 * failure, so a signed-in user keeps their session and comes back to where they
 * were when maintenance ends.
 */
export interface MaintenanceStatus {
  enabled: boolean;
  blocksReads: boolean;
  messageEn: string;
  messageId: string;
}

export const MAINTENANCE_OFF: MaintenanceStatus = {
  enabled: false,
  blocksReads: false,
  messageEn: "",
  messageId: "",
};

let current: MaintenanceStatus = MAINTENANCE_OFF;
const listeners = new Set<() => void>();

export function getMaintenance(): MaintenanceStatus {
  return current;
}

export function subscribeMaintenance(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setMaintenance(next: MaintenanceStatus): void {
  if (
    next.enabled === current.enabled &&
    next.blocksReads === current.blocksReads &&
    next.messageEn === current.messageEn &&
    next.messageId === current.messageId
  ) {
    return;
  }
  current = next;
  listeners.forEach((listener) => listener());
}

export function useMaintenance(): MaintenanceStatus {
  return useSyncExternalStore(subscribeMaintenance, getMaintenance, getMaintenance);
}

/** True when maintenance closes the app (and sign-in), not only writes. */
export function isClosed(status: MaintenanceStatus): boolean {
  return status.enabled && status.blocksReads;
}

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * Records a 503 MAINTENANCE answer. A refused read means everything is closed;
 * a refused write says only that maintenance is on, so what was known about
 * reads is kept.
 */
export function reportMaintenance(method: string, details: Record<string, unknown> | undefined): void {
  const safeRead = ["GET", "HEAD", "OPTIONS"].includes(method.toUpperCase());
  setMaintenance({
    enabled: true,
    blocksReads: safeRead ? true : current.blocksReads,
    messageEn: text(details?.message_en) || current.messageEn,
    messageId: text(details?.message_id) || current.messageId,
  });
}

/**
 * Reads the public status answer. Anything that is not the expected shape is
 * unknown (null), never "maintenance is over": a CDN's fallback page or a
 * half-written answer must not reopen an app that is closed.
 */
export function statusFromResponse(data: unknown): MaintenanceStatus | null {
  if (typeof data !== "object" || data === null || Array.isArray(data)) return null;
  const record = data as Record<string, unknown>;
  if (typeof record.enabled !== "boolean") return null;
  const enabled = record.enabled;
  return {
    enabled,
    blocksReads: enabled && record.blocks_reads === true,
    messageEn: text(record.message_en),
    messageId: text(record.message_id),
  };
}

/** The admin's message in the user's language, or the other language, or nothing. */
export function maintenanceMessage(status: MaintenanceStatus, locale: Locale): string {
  const [first, second] =
    locale === "id" ? [status.messageId, status.messageEn] : [status.messageEn, status.messageId];
  return first || second;
}
